import { useEffect, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { FieldShell } from './FieldShell';
import { FieldDate } from './FieldDate';
import { useFieldName, useDomFieldId } from './_useFieldName';
import { onResolveAutoComplete, FIELD_NO_AUTOFILL, onNoAutofillProps, onResolveFriendlyPlaceholder } from './_fieldDefaults';

const BLOCK = 'LStudio__app_field';

/*
    🔥 INPUT — text, email, password, number, tel, url, search, date, time

    Sin autocompletado ni autofill. Si en algún campo SÍ lo querés
    (una dirección de envío, por ejemplo), pasale autoComplete="street-address"
    y noAutofill={false}.
*/

export const FieldInput = ({
    id, name, label, hint, error, type = 'text',
    value, onChange, placeholder,
    iconStart = null, iconEnd = null,
    autoComplete = null, noAutofill = true,
    isRequired = false, isDisabled = false, isReadOnly = false,
    span = 1, onFocus = null, onBlur = null, min = null, max = null, ...rest
}) => {

    const [useIsVisible, setIsVisible] = useState(false);

    /*
        type="pin" se ve como una contraseña pero para el navegador es un
        campo de texto comun: no aparece el cartel de "¿guardar contraseña?".
    */
    const IS_PIN = type === 'pin';

    const IS_PASSWORD = type === 'password' || IS_PIN;

    const REAL_TYPE = IS_PIN ? 'text' : (IS_PASSWORD && useIsVisible ? 'text' : type);

    // Un secreto revelado no queda visible indefinidamente. También se oculta
    // apenas la pestaña deja de estar activa.
    useEffect(() => {
        if (!IS_PASSWORD || !useIsVisible) return undefined;

        const timer = window.setTimeout(() => setIsVisible(false), 30 * 1000);
        const onVisibility = () => {
            if (document.hidden) setIsVisible(false);
        };

        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [IS_PASSWORD, useIsVisible]);

    const GUARD = noAutofill ? onNoAutofillProps({ isReadOnly, isDisabled }) : {};

    const { domName, onChangeField, onBlurField } = useFieldName({ id, name, onChange, onBlur, isEnabled: noAutofill });

    const onHandleFocus = (event) => {
        GUARD.onFocus?.(event);
        onFocus?.(event);
    };

    const DOM_ID = useDomFieldId(id);

    const RESOLVED_PLACEHOLDER = onResolveFriendlyPlaceholder({ placeholder, label, type });

    const IS_APP_DATE = type === 'date' || type === 'datetime-local';

    const onParseLocalDate = (raw) => {
        if (!raw) return null;
        if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
        const text = String(raw).trim();
        const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnly) {
            const [, year, month, day] = dateOnly;
            return new Date(Number(year), Number(month) - 1, Number(day));
        }
        const normalized = text.includes('T') ? text : text.replace(' ', 'T');
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const onPad = (valueToPad) => String(valueToPad).padStart(2, '0');
    const onFormatLocalDate = (date, withTime) => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
        const day = `${date.getFullYear()}-${onPad(date.getMonth() + 1)}-${onPad(date.getDate())}`;
        if (!withTime) return day;
        return `${day}T${onPad(date.getHours())}:${onPad(date.getMinutes())}`;
    };

    if (IS_APP_DATE) {
        const WITH_TIME = type === 'datetime-local';
        return (
            <FieldDate
                id={DOM_ID}
                name={name ?? id}
                label={label}
                hint={hint}
                error={error}
                value={onParseLocalDate(value)}
                onChange={(date) => onChange?.({ target: { name: name ?? id, value: onFormatLocalDate(date, WITH_TIME) } })}
                placeholder={placeholder ?? (WITH_TIME ? 'dd/mm/aaaa hh:mm' : 'dd/mm/aaaa')}
                showTime={WITH_TIME}
                minDate={onParseLocalDate(min)}
                maxDate={onParseLocalDate(max)}
                isRequired={isRequired}
                isDisabled={isDisabled || isReadOnly}
                span={span}
                {...rest}
            />
        );
    }

    return (
        <FieldShell id={DOM_ID} label={label} hint={hint} error={error}
            isRequired={isRequired} isDisabled={isDisabled} span={span} >

            <div className={`${BLOCK}__wrap`}>

                {iconStart && <span className={`${BLOCK}__icon ${BLOCK}__icon--start`}>{iconStart}</span>}

                <input id={DOM_ID} data-field={name ?? id} name={domName} type={REAL_TYPE}
                    className={`${BLOCK}__input`}
                    value={value ?? ''} onChange={onChangeField} onBlur={onBlurField}
                    placeholder={RESOLVED_PLACEHOLDER}
                    autoComplete={onResolveAutoComplete(IS_PIN ? 'text' : type, autoComplete)}
                    required={isRequired} disabled={isDisabled}
                    aria-invalid={!!error}
                    data-has-start={!!iconStart} data-has-end={!!iconEnd || IS_PASSWORD}
                    data-masked={IS_PIN ? String(!useIsVisible) : undefined}
                    {...FIELD_NO_AUTOFILL}
                    {...GUARD}
                    readOnly={isReadOnly || GUARD.readOnly}
                    onFocus={onHandleFocus}
                    {...rest} />

                {
                    IS_PASSWORD
                        ? (
                            <button type="button" className={`${BLOCK}__icon ${BLOCK}__icon--end ${BLOCK}__icon--button`}
                                aria-label={useIsVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                onClick={() => setIsVisible(!useIsVisible)} >
                                {useIsVisible ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        )
                        : iconEnd && <span className={`${BLOCK}__icon ${BLOCK}__icon--end`}>{iconEnd}</span>
                }

            </div>

        </FieldShell>
    );
};
